import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { UploadCloud, FileCheck2, Loader2, BookUser as Passport, ExternalLink, PartyPopper, Info } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { COUNTRIES, MONTHS, ENGLISH_LEVELS, PASSPORT_GUIDES, WHATSAPP_LINK } from "@/data/constants";
import { useLang } from "@/context/LangContext";

const inputCls =
  "w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-sm text-[#0B132B] placeholder:text-slate-400 focus:outline-none focus:border-[#FFD166] transition-colors";
const labelCls = "block text-sm font-medium text-slate-700 mb-1.5";

export const CandidateForm = ({ selectedJob }) => {
  const { t, monthLabel } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: "", country_code: "+52", phone: "", email: "", country: "México",
    state_city: "", age: "", passport_status: "tiene", travel_month: "", english_level: "ninguno",
  });
  const [doc, setDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [accept, setAccept] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const guide = PASSPORT_GUIDES[form.country] || PASSPORT_GUIDES.default;

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDoc(data);
      toast.success(t("form.okDoc"));
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.travel_month) {
      toast.error(t("form.errFields"));
      return;
    }
    if (!accept) {
      toast.error(t("form.errTerms"));
      return;
    }
    if (form.passport_status === "tiene" && !doc) {
      toast.error("Debes subir una foto o PDF de tu pasaporte.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/candidates", {
        ...form,
        age: form.age ? parseInt(form.age, 10) : null,
        document_id: doc?.document_id || null,
        document_filename: doc?.filename || null,
        job_title: selectedJob?.title || "SHEIN Polonia - Logística",
        accept_terms: accept,
      });
      const goNext = () => setTimeout(() => {
        navigate("/siguiente-paso", { state: {
          full_name: form.full_name, phone: form.phone, country_code: form.country_code,
          email: form.email, city: form.state_city,
        } });
      }, 1400);
      if (data?.duplicate) {
        if (data.stage === "etapa2_done") {
          toast.info("Ya te registraste y confirmaste tu viaje.");
        } else {
          toast.info("Ya te registraste antes, pero aún no completas la Etapa 2. Te llevamos para completarla.");
          setDone(true);
          goNext();
        }
      } else {
        setDone(true);
        toast.success(t("form.okSubmit"));
        goNext();
      }
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section id="aplicar" className="py-16 md:py-24 bg-[#F5F7FB]">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="bg-white border border-[#16a34a]/40 rounded-3xl p-10 shadow-lg">
            <PartyPopper className="mx-auto text-[#16a34a]" size={56} />
            <h2 className="font-head font-extrabold text-3xl mt-6 text-[#0B132B]">{t("form.thanks")} {form.full_name.split(" ")[0]}!</h2>
            <p className="text-slate-600 mt-3">{t("form.thanksMsg")}</p>
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="success-whatsapp-btn"
              className="inline-flex items-center gap-2 mt-6 bg-[#25D366] text-white font-bold px-7 py-3.5 rounded-full">
              {t("form.thanksWa")}
            </a>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="aplicar" className="py-16 md:py-24 bg-[#F5F7FB] scroll-mt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className="text-[#E63946] text-sm font-semibold uppercase tracking-wider">{t("form.eyebrow")}</span>
          <h2 className="font-head font-extrabold text-3xl md:text-5xl mt-2 text-[#0B132B]">{t("form.title")}</h2>
          <p className="text-slate-600 mt-3 max-w-xl mx-auto">
            {selectedJob ? <>{t("form.applyingTo")} <strong className="text-[#0B132B]">{selectedJob.title}</strong></> : t("form.subtitle")}
          </p>
          <div className="ej-accent-bar mx-auto mt-4" />
        </div>

        <form onSubmit={submit} data-testid="candidate-application-form"
          className="bg-white rounded-3xl border border-slate-200 shadow-xl p-6 md:p-10 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>{t("form.name")}</label>
              <input data-testid="cf-name" className={inputCls} value={form.full_name}
                onChange={(e) => set("full_name", e.target.value)} placeholder={t("form.namePh")} />
            </div>
            <div>
              <label className={labelCls}>{t("form.country")}</label>
              <select data-testid="cf-country" className={inputCls} value={form.country}
                onChange={(e) => {
                  const c = COUNTRIES.find((x) => x.name === e.target.value);
                  set("country", e.target.value);
                  if (c) set("country_code", c.code);
                }}>
                {COUNTRIES.map((c) => <option key={c.name} value={c.name}>{c.flag} {c.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>{t("form.phone")}</label>
              <div className="flex gap-2">
                <select data-testid="cf-code" className={`${inputCls} w-28`} value={form.country_code}
                  onChange={(e) => set("country_code", e.target.value)}>
                  {COUNTRIES.map((c) => <option key={c.code + c.name} value={c.code}>{c.code}</option>)}
                </select>
                <input data-testid="cf-phone" className={inputCls} value={form.phone}
                  onChange={(e) => set("phone", e.target.value)} placeholder={t("form.phonePh")} />
              </div>
            </div>
            <div>
              <label className={labelCls}>{t("form.email")}</label>
              <input data-testid="cf-email" type="email" className={inputCls} value={form.email}
                onChange={(e) => set("email", e.target.value)} placeholder={t("form.emailPh")} />
            </div>
            <div>
              <label className={labelCls}>{t("form.city")}</label>
              <input data-testid="cf-city" className={inputCls} value={form.state_city}
                onChange={(e) => set("state_city", e.target.value)} placeholder={t("form.cityPh")} />
            </div>
            <div>
              <label className={labelCls}>{t("form.age")}</label>
              <input data-testid="cf-age" type="number" min="18" max="60" className={inputCls} value={form.age}
                onChange={(e) => set("age", e.target.value)} placeholder={t("form.agePh")} />
            </div>
          </div>

          {/* Passport status */}
          <div>
            <label className={labelCls}>{t("form.passportQ")}</label>
            <div className="grid grid-cols-2 gap-3">
              {[
                { v: "tiene", l: t("form.hasPassport") },
                { v: "no_tiene", l: t("form.noPassport") },
              ].map((o) => (
                <button type="button" key={o.v} data-testid={`cf-passport-${o.v}`}
                  onClick={() => set("passport_status", o.v)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${
                    form.passport_status === o.v
                      ? "border-[#FFD166] bg-[#FFF6DC] text-[#B8860B]"
                      : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
                  }`}>
                  <Passport size={16} /> {o.l}
                </button>
              ))}
            </div>
          </div>

          <AnimatePresence>
            {form.passport_status === "no_tiene" && (
              <motion.div data-testid="passport-guidance-tool"
                initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden">
                <div className="bg-[#FFFBEB] border border-[#F59E0B]/40 rounded-2xl p-5">
                  <div className="flex items-center gap-2 text-[#B45309] font-semibold">
                    <Info size={18} /> {t("form.guideHow")} {form.country}
                  </div>
                  <div className="text-sm text-slate-700 mt-3">
                    <div><strong>{t("form.gAuthority")}</strong> {guide.authority}</div>
                    <div><strong>{t("form.gTime")}</strong> {guide.time}</div>
                    <div><strong>{t("form.gCost")}</strong> {guide.cost}</div>
                  </div>
                  <ol className="mt-3 space-y-1.5 text-sm text-slate-700 list-decimal list-inside">
                    {guide.steps.map((s, i) => <li key={i}>{s}</li>)}
                  </ol>
                  <a href={guide.link} target="_blank" rel="noreferrer" data-testid="passport-guide-link"
                    className="inline-flex items-center gap-1.5 mt-4 text-[#B45309] text-sm font-semibold hover:underline">
                    {t("form.gLink")} <ExternalLink size={14} />
                  </a>
                  <p className="text-xs text-slate-500 mt-3">{t("form.gNote")}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Travel month */}
          <div>
            <label className={labelCls}>{t("form.travelQ")}</label>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {MONTHS.map((m) => (
                <button type="button" key={m} data-testid={`cf-month-${m}`}
                  onClick={() => set("travel_month", m)}
                  className={`px-2 py-2.5 rounded-lg text-xs font-medium border transition-all ${
                    form.travel_month === m
                      ? "border-[#FFD166] bg-[#FFD166] text-[#0B132B]"
                      : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
                  }`}>
                  {monthLabel(m)}
                </button>
              ))}
            </div>
          </div>

          {/* English level */}
          <div>
            <label className={labelCls}>{t("form.englishLabel")} <span className="text-slate-400 font-normal">{t("form.englishOpt")}</span></label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {ENGLISH_LEVELS.map((lv) => (
                <button type="button" key={lv.value} data-testid={`cf-english-${lv.value}`}
                  onClick={() => set("english_level", lv.value)}
                  className={`px-3 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                    form.english_level === lv.value
                      ? "border-[#16a34a] bg-[#DCFCE7] text-[#16a34a]"
                      : "border-slate-300 bg-white text-slate-600 hover:border-slate-400"
                  }`}>
                  {t(`form.english.${lv.value}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Upload */}
          <div>
            <label className={labelCls}>{t("form.uploadLabel")} <span className="text-slate-400 font-normal">{t("form.uploadOpt")}</span></label>
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
              data-testid="cf-upload-zone"
              className="border-2 border-dashed border-slate-300 rounded-2xl p-6 text-center cursor-pointer hover:border-[#FFD166] transition-colors bg-[#F8FAFC]">
              <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" data-testid="cf-file-input"
                onChange={(e) => handleFile(e.target.files?.[0])} />
              {uploading ? (
                <div className="flex flex-col items-center text-slate-500"><Loader2 className="animate-spin text-[#B8860B]" /><span className="text-sm mt-2">{t("form.uploading")}</span></div>
              ) : doc ? (
                <div className="flex flex-col items-center text-[#16a34a]"><FileCheck2 size={28} /><span className="text-sm mt-2 text-slate-700">{doc.filename}</span><span className="text-xs text-slate-400">{t("form.uploadChange")}</span></div>
              ) : (
                <div className="flex flex-col items-center text-slate-500"><UploadCloud size={28} className="text-[#B8860B]" /><span className="text-sm mt-2">{t("form.uploadDrop")}</span><span className="text-xs mt-1">{t("form.uploadHint")}</span></div>
              )}
            </div>
          </div>

          <label className="flex items-start gap-3 text-sm text-slate-600 cursor-pointer">
            <input type="checkbox" data-testid="cf-terms" checked={accept} onChange={(e) => setAccept(e.target.checked)}
              className="mt-1 accent-[#FFD166] w-4 h-4" />
            {t("form.terms")}
          </label>

          <button type="submit" disabled={submitting} data-testid="cf-submit"
            className="w-full bg-[#FFD166] text-[#0B132B] font-bold py-4 rounded-full hover:bg-[#E0A926] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
            {submitting && <Loader2 className="animate-spin" size={18} />}
            {t("form.submit")}
          </button>
        </form>
      </div>
    </section>
  );
};
